import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faClose } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Skeleton } from '@sorare/core/src/atoms/animations/Skeleton';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, LabelS, LabelXS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { AmountWithConversion } from '@sorare/core/src/components/buyActions/AmountWithConversion';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import type { CardHit } from '@sorare/core/src/lib/algolia';
import { convertCardHitToCard } from '@sorare/core/src/lib/algolia';
import type { MonetaryAmountParams } from '@sorare/core/src/lib/monetaryAmount';

import { CardProperties } from '#src/components/card/CardProperties/index.tsx';
import { PriceHistoryTooltip } from '#src/components/price/PriceHistoryTooltip/index.tsx';
import { CardDescription } from '#src/components/token/CardDescription/index.tsx';
import { FlexCard } from '#src/components/token/FlexCard/index.tsx';
import { useGetTokenSingleSaleDetails } from '#src/hooks/offers/useGetTokenSingleSaleDetails.ts';
import { useIsPrimaryBuyLive } from '#src/hooks/primaryBuy/useIsPrimaryBuyLive.ts';

import type {
  CardRow_anyCard,
  CardRow_anyCardBaseData,
  DirectOffer_CardRow_anyCard,
} from './__generated__/index.graphql.ts';

type BaseProps = {
  children?: React.ReactNode;
  onRemove?: () => void;
  cardData?: DirectOffer_CardRow_anyCard;
  withLinks?: boolean;
};

type NoMinPriceProps = BaseProps & {
  displayMinPrice?: never;
  minPriceCurrency?: never;
};

type WithMinPriceProps = BaseProps & {
  displayMinPrice: boolean;
};

type Props = NoMinPriceProps | WithMinPriceProps;

const MinimumPriceDisplay = ({
  publicMinPrices,
}: {
  publicMinPrices: MonetaryAmountParams;
}) => {
  const { toMonetaryAmount } = useMonetaryAmount();
  const { main } = useAmountWithConversion({
    monetaryAmount: toMonetaryAmount(publicMinPrices),
  });

  return (
    <LabelS color="var(--c-nd-600)">
      <FormattedMessage
        defaultMessage="The seller set a minimum price of {amount}"
        values={{
          amount: main,
        }}
      />
    </LabelS>
  );
};

const Root = styled(Vertical).attrs({ gap: 1 })`
  width: 100%;
  padding: var(--unit) var(--intermediate-unit);
  &.withClearButton {
    padding: var(--unit) var(--quadruple-unit) var(--unit)
      var(--intermediate-unit);
  }
`;

const Card = styled.div`
  width: 44px;
`;
const Container = styled(Vertical)`
  flex: 1;
  justify-content: flex-start;
  align-items: flex-start;
`;
const Description = styled.div`
  align-items: flex-start;
  text-align: left;
`;

const PriceSkeleton = styled(Skeleton)`
  width: 100px;
  height: 40px;
`;

const CardPropertiesSkeleton = styled(Skeleton)`
  width: 100px;
  height: 24px;
`;

const ClearButton = styled(IconButton).attrs({ color: 'tertiary' })`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translate(50%, -50%);
`;

const PriceColumn = styled(Vertical)`
  align-items: flex-end;
  gap: var(--half-unit);

  &:last-child {
    margin-top: var(--half-unit);
  }
`;

type CardRowProps = {
  card: CardRow_anyCardBaseData;
  cardData?: CardRow_anyCard;
} & Omit<Props, 'cardData'>;

export const CardRow = ({
  card,
  cardData,
  displayMinPrice,
  children,
  onRemove,
  withLinks,
}: CardRowProps) => {
  const { toMonetaryAmount } = useMonetaryAmount();
  const sameUtilityLowestPriceCard = cardData?.inSeasonEligible
    ? cardData?.lowestPriceCard
    : cardData?.lowestPriceCardAnySeason;
  const offer = useGetTokenSingleSaleDetails(sameUtilityLowestPriceCard);
  const primaryBuy = useIsPrimaryBuyLive(sameUtilityLowestPriceCard);
  const monetaryAmount = (primaryBuy || offer)?.price;

  return (
    <Root className={classNames({ withClearButton: !!onRemove })}>
      <Horizontal>
        <Card>
          <FlexCard card={card} width={40} withLink={withLinks} />
        </Card>
        <Container>
          <Description>
            <CardDescription
              card={card}
              withoutLink={!withLinks}
              detailsColor="var(--c-nd-600)"
              Details={LabelS}
            />
          </Description>
          {!!cardData?.publicMinPrices && !!displayMinPrice && (
            <MinimumPriceDisplay
              publicMinPrices={toMonetaryAmount(cardData.publicMinPrices)}
            />
          )}
          {cardData ? (
            <CardProperties
              playerScoreSmall
              card={cardData}
              withTransferMalus
            />
          ) : (
            <CardPropertiesSkeleton />
          )}
          {!!cardData?.sealed && (
            <LabelXS color="var(--c-red-300)">
              <FormattedMessage defaultMessage="Sealed cards cannot be included in a trade" />
            </LabelXS>
          )}
        </Container>
        {cardData ? (
          <PriceColumn gap={0}>
            <LabelXS bold color="var(--c-nd-600)">
              <FormattedMessage defaultMessage="Floor price" />
            </LabelXS>
            <BodyS>
              {monetaryAmount ? (
                <AmountWithConversion
                  monetaryAmount={monetaryAmount}
                  hideExponent
                />
              ) : (
                '-'
              )}
            </BodyS>
            <PriceHistoryTooltip
              card={cardData}
              Label={props => (
                <Chip {...props}>
                  <FormattedMessage defaultMessage="Recent sales" />
                </Chip>
              )}
            />
          </PriceColumn>
        ) : (
          <PriceSkeleton />
        )}
      </Horizontal>
      {children}
      {!!onRemove && (
        <ClearButton size="medium" onClick={onRemove} icon={faClose} />
      )}
    </Root>
  );
};

CardRow.fragments = {
  anyCardBaseData: gql`
    fragment CardRow_anyCardBaseData on AnyCardInterface {
      slug
      ...CardDescription_anyCard
      ...FlexCard_anyCard
    }
    ${CardDescription.fragments.anyCard}
    ${FlexCard.fragments.anyCard}
  ` as TypedDocumentNode<CardRow_anyCardBaseData>,
  anyCard: gql`
    fragment CardRow_anyCard on AnyCardInterface {
      slug
      publicMinPrices {
        ...useMonetaryAmount_monetaryAmount
      }
      sealed
      inSeasonEligible
      lowestPriceCard {
        slug
        ...useIsPrimaryBuyLive_anyCard
        ...useGetTokenSingleSaleDetails_anyCard
      }
      lowestPriceCardAnySeason {
        slug
        ...useIsPrimaryBuyLive_anyCard
        ...useGetTokenSingleSaleDetails_anyCard
      }
      ...FlexCard_anyCard
      ...CardDescription_anyCard
      ...PriceHistoryTooltip_anyCard
      ...CardProperties_anyCard
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
    ${FlexCard.fragments.anyCard}
    ${CardDescription.fragments.anyCard}
    ${useGetTokenSingleSaleDetails.fragments.anyCard}
    ${useIsPrimaryBuyLive.fragments.anyCard}
    ${PriceHistoryTooltip.fragments.anyCard}
    ${CardProperties.fragments.anyCard}
  ` as TypedDocumentNode<CardRow_anyCard>,
};

export const CardRowFromHit = ({
  card,
  ...rest
}: Props & { card: CardHit }) => {
  const convertedCardHit = useMemo(() => convertCardHitToCard(card), [card]);
  return <CardRow card={convertedCardHit} {...rest} />;
};

CardRowFromHit.fragments = {
  anyCard: gql`
    fragment DirectOffer_CardRow_anyCard on AnyCardInterface {
      slug
      ...CardRow_anyCard
    }
    ${CardRow.fragments.anyCard}
  ` as TypedDocumentNode<DirectOffer_CardRow_anyCard>,
};
